import React, { useEffect, useRef, useMemo } from 'react';
import { createPluginUI } from 'molstar/lib/mol-plugin-ui';
import { ColorNames } from 'molstar/lib/mol-util/color/names';
import 'molstar/lib/mol-plugin-ui/skin/light.scss';
import { renderReact18 } from 'molstar/lib/mol-plugin-ui/react18';
import { PluginCommands } from 'molstar/lib/mol-plugin/commands';
import { Script } from 'molstar/lib/mol-script/script';
import { StructureSelection, StructureElement, StructureProperties } from 'molstar/lib/mol-model/structure';
import { MolScriptBuilder as MS } from 'molstar/lib/mol-script/language/builder';
import { StateTransforms } from 'molstar/lib/mol-plugin-state/transforms';
import { createStructureRepresentationParams } from 'molstar/lib/mol-plugin-state/helpers/structure-representation-params';
import { Color } from "molstar/lib/mol-util/color";
import Swal from 'sweetalert2'
import { alignAndSuperposeWithSIFTSMapping } from 'molstar/lib/mol-model/structure/structure/util/superposition-sifts-mapping';
import { Mat4 } from 'molstar/lib/mol-math/linear-algebra';
import { StateObjectCell, StateObjectRef } from 'molstar/lib/mol-state';
import { StructureSuperpositionParams } from 'molstar/lib/commonjs/mol-plugin-ui/structure/superposition'

/* ══════════════════════════════════════════════════════════════════════
   COLOR MAPS — aligned with FunctionalAnnotationStructureLegend
   Hex values are stored WITHOUT the '#' prefix because Mol* consumes
   them via `Color("0x" + hex)` and the sequence-span CSS as `#${hex}`.

   Matching strategy: all dynamic fields come from UniProt/DSSP/PSI-MOD
   controlled vocabularies like:
     "Signal peptide [SO:0000418]"
     "Alpha helix [DSSP:H]"
     "S-palmitoyl cysteine [MOD:00040]"
   so we match by case-insensitive substring against short keyword lists.
   ══════════════════════════════════════════════════════════════════════ */

// unAnnNatVariants — legend: rgb(178, 24, 43) → #b2182b
const VARIANT_SPACEFILL_COLOR_HEX = 0xb2182b;

// unSecondaryStructure (from DSSP, curated) — substring keys
const SECONDARY_STRUCTURE_KEYWORDS = [
    { key: 'alpha helix',    hex: '838ffc' },
    { key: 'beta strand',    hex: 'da54a3' },
    { key: 'turn',           hex: 'fca311' },
];

// afPredictedSecondaryStructure — AlphaFold-predicted SS categories
// (legend groups all helices under one color bucket)
const AF_SECONDARY_STRUCTURE_KEYWORDS = [
    { key: 'alpha helix',       hex: 'FF0080' },
    { key: '3_10 helix',        hex: 'FF0080' },
    { key: 'pi helix',          hex: 'FF0080' },
    { key: 'polyproline helix', hex: 'FF0080' },
    { key: 'beta strand',       hex: 'FFC800' },
    { key: 'turn',              hex: '00B266' },
    { key: 'bend',              hex: '66D8C9' },
    { key: 'disordered',        hex: 'e63946' },
    { key: 'coil',              hex: 'adb5bd' },
];

// unLocation — Topological domain / Transmembrane region / Intramembrane region
const LOCATION_KEYWORDS = [
    { key: 'topological domain',   hex: '0c7489' },
    { key: 'transmembrane region', hex: 'da54a3' },
    { key: 'intramembrane region', hex: '820e44' },
];

// unMoleculeProcessing — 6 categories in the legend
const MOLECULE_PROCESSING_KEYWORDS = [
    { key: 'mature protein',        hex: '838ffc' },
    { key: 'signal peptide',        hex: 'da54a3' },
    { key: 'transit peptide',       hex: '820e44' },
    { key: 'propeptide',            hex: 'c746d4' },
    { key: 'initiator methionine',  hex: '20b2aa' },
    { key: 'active peptide',        hex: 'f3722c' },
];

// phosPtms — PhosphoSitePlus short codes OR PSI-MOD standard labels
// Both flavors are supported so data in either form resolves correctly.
const PTM_COLOR_MAP = {
    // PhosphoSitePlus short codes
    p:  '4393C3',
    ac: '5aae61',
    ub: 'bf812d',
    sm: 'd6604d',
    m:  'c51b7d',
    ga: 'e08214',
    gl: 'fdb863',
};
const PTM_LABEL_KEYWORDS = [
    { key: 'phosphorylation',        hex: '4393C3' },
    { key: 'acetylation',            hex: '5aae61' },
    { key: 'ubiquitination',         hex: 'bf812d' },
    { key: 'sumoylation',            hex: 'd6604d' },
    { key: 'methylation',            hex: 'c51b7d' },
    { key: 'o-galnac glycosylation', hex: 'e08214' },
    { key: 'o-glcnac glycosylation', hex: 'fdb863' },
];
const PTM_MULTI_COLOR_HEX = '66D8C9';
const PTM_DEFAULT_HEX = '666666';

// unGlycosylations — 4 subtypes (from CARBOHYD_SPECIFIC_MAP)
const GLYCOSYLATION_KEYWORDS = [
    { key: 'n-linked', hex: '56ab2f' },
    { key: 'o-linked', hex: '9d4edd' },
    { key: 'c-linked', hex: 'd90368' },
    { key: 's-linked', hex: '00b4d8' },
];
const GLYCOSYLATION_DEFAULT_HEX = '56ab2f';

// unLipidations — 10 subtypes (from LIPID_SPECIFIC_MAP keywords)
// Keywords are the short markers the Python side uses to look up /note= content.
const LIPIDATION_KEYWORDS = [
    { key: 'palmitoleyl',     hex: '1d3557' }, // O-palmitoleyl serine — before 'palmitoyl'
    { key: 'palmitoyl',       hex: '007f5f' }, // S-palmitoyl cysteine
    { key: 'myristoyl',       hex: '2a9d8f' }, // N-myristoyl glycine
    { key: 'geranylgeranyl',  hex: 'f4a261' }, // S-geranylgeranyl cysteine — before 'farnesyl'
    { key: 'farnesyl',        hex: 'e9c46a' }, // S-farnesyl cysteine
    { key: 'gpi',             hex: '264653' }, // GPI-anchor
    { key: 'cholesterol',     hex: 'e76f51' }, // Cholesterol ester
    { key: 'lipoyl',          hex: '8ab17d' }, // Lipoylation
    { key: 'stearoyl',        hex: '457b9d' }, // S-stearoyl cysteine
    { key: 'octanoyl',        hex: '6a4c93' }, // O-octanoyl serine
];
const LIPIDATION_DEFAULT_HEX = '007f5f';

/**
 * Find a color by case-insensitive substring match against an ordered
 * keyword list. Order matters when one keyword is a substring of another
 * (e.g. 'palmitoyl' vs 'palmitoleyl', 'farnesyl' vs 'geranylgeranyl').
 */
const matchByKeyword = (value, keywordList, defaultHex) => {
    if (!value) return defaultHex;
    const lower = String(value).toLowerCase();
    const hit = keywordList.find(({ key }) => lower.includes(key));
    return hit ? hit.hex : defaultHex;
};

/**
 * Resolve the color for a PTM value. Handles:
 *   - multi-PTM (semicolon-separated tokens, or multiple short codes) → multi color
 *   - PhosphoSitePlus short codes ("p", "p-Ser", "ac", "ub", …)
 *   - PSI-MOD labels ("Phosphorylation [MOD:00696]", …)
 */
const getPtmColorHex = (ptmValue) => {
    if (!ptmValue) return PTM_DEFAULT_HEX;
    const trimmed = String(ptmValue).trim();

    // Strip bracketed annotations like "[MOD:00696]" or "[Ser]" before
    // deciding whether this is a multi-PTM cell — otherwise the internal
    // space would look like a separator.
    const stripped = trimmed.replace(/\[[^\]]*\]/g, '').trim();

    // Multi-PTM: prefer semicolons; fall back to whitespace only if the
    // remaining tokens are all short codes (length ≤ 3), which is how
    // PhosphoSitePlus concatenates them ("p ac ub").
    const semiTokens = stripped.split(';').map(s => s.trim()).filter(Boolean);
    if (semiTokens.length > 1) return PTM_MULTI_COLOR_HEX;

    const wsTokens = stripped.split(/\s+/).filter(Boolean);
    if (wsTokens.length > 1 && wsTokens.every(t => t.length <= 3)) {
        return PTM_MULTI_COLOR_HEX;
    }

    const token = stripped || trimmed;
    const lower = token.toLowerCase();

    // PSI-MOD long-label form — try this first so "methylation" matches
    // the word, not the 'm' short code.
    const label = matchByKeyword(lower, PTM_LABEL_KEYWORDS, null);
    if (label) return label;

    // Short-code form: "p", "p-Ser", "ac-Lys", "ub", "sm", "m", "ga", "gl"
    const head = lower.split('-')[0];
    if (PTM_COLOR_MAP[head]) return PTM_COLOR_MAP[head];

    return PTM_DEFAULT_HEX;
};

// ──────────────────────────────────────────────────────────────────────

// Define the transform function here
async function transform(s, matrix, coordinateSystem, plugin) {
    const r = StateObjectRef.resolveAndCheck(plugin.state.data, s);
    if (!r) return;

    const o = plugin.state.data.selectQ(q => q.byRef(r.transform.ref).subtree().withTransformer(StateTransforms.Model.TransformStructureConformation))[0];

    const transformMatrix = coordinateSystem && !Mat4.isIdentity(coordinateSystem.matrix)
              ? Mat4.mul(Mat4(), coordinateSystem.matrix, matrix)
              : matrix;

    const params = {
              transform: {
                name: 'matrix',
                params: { data: transformMatrix, transpose: false }
              }
    };

    const b = o
              ? plugin.state.data.build().to(o).update(params)
              : plugin.state.data.build().to(s)
                .insert(StateTransforms.Model.TransformStructureConformation, params, { tags: 'SuperpositionTransform' });

    await plugin.runTask(plugin.state.data.updateTree(b));
}

const DYNAMIC_STYLE_ID_PREFIX_BASE = 'molstar-seq-highlight-style-';

// Set a React-controlled <select> value and fire a change event React honours.
function setNativeSelectValue(selectEl, value) {
    const setter = Object.getOwnPropertyDescriptor(window.HTMLSelectElement.prototype, 'value').set;
    setter.call(selectEl, value);
    selectEl.dispatchEvent(new Event('change', { bubbles: true }));
}

// Switch the sequence panel to a mode that lists every polymer chain, so each
// chain gets its own labelled wrapper we can scope styling to.
function switchToAllChainsMode(viewerRootElement) {
    const modeSelect = viewerRootElement.querySelector('select.msp-form-control[title^="[Mode]"]');
    if (!modeSelect) return;
    const opt = Array.from(modeSelect.options).find(o => o.value === 'polymers')
             || Array.from(modeSelect.options).find(o => o.value === 'all');
    if (opt && modeSelect.value !== opt.value) setNativeSelectValue(modeSelect, opt.value);
}

// Resolve the element whose residue spans belong to `targetChain`.
//   - returns that element to scope span-styling to;
//   - returns null when the wrong chain is still on screen (caller should
//     switch mode and wait for the re-render);
//   - a null targetChain means "no specific chain" (monomer / homodimer) and
//     the whole panel is used, preserving the original behaviour.
function resolveSequenceScope(viewerRootElement, targetChain) {
    if (!targetChain) return viewerRootElement;

    const chainLabels = viewerRootElement.querySelectorAll('.msp-sequence-chain-label');
    if (chainLabels.length > 0) {
        // polymers / all mode: pick the wrapper following the matching label
        for (const lbl of chainLabels) {
            const chainId = (lbl.textContent || '').split('|')[0].trim();
            if (chainId === targetChain) {
                let el = lbl.nextElementSibling;
                while (el && !(el.classList && el.classList.contains('msp-sequence-wrapper'))) {
                    el = el.nextElementSibling;
                }
                return el || null;
            }
        }
        return null;
    }

    // single mode: one wrapper; the chain is whatever the Chain select shows
    const chainSelect = viewerRootElement.querySelector('select.msp-form-control[title^="[Chain]"]');
    const shown = chainSelect
        ? (chainSelect.options[chainSelect.selectedIndex]?.textContent || '').trim()
        : 'A';
    if (!chainSelect || shown === targetChain) return viewerRootElement;
    return null; // wrong chain visible -> needs a mode switch
}

// NEW: Robust observer setup (chain-aware)
function observeAndStyleSequence(viewerRootElement, stylingFn, observerRef, targetChain = null) {

    if (!viewerRootElement) return;
    if (observerRef.current) observerRef.current.disconnect();

    const styleContainer = () => {
        if (!viewerRootElement.querySelector('.msp-sequence-wrapper')) return;
        const scope = resolveSequenceScope(viewerRootElement, targetChain);
        if (scope === null) {
            // target chain not visible yet: expose all chains; the resulting
            // re-render re-triggers this observer, which then finds the wrapper
            switchToAllChainsMode(viewerRootElement);
            return;
        }
        stylingFn(scope);
    };
    styleContainer();
    const observer = new MutationObserver(styleContainer);
    observer.observe(viewerRootElement, { childList: true, subtree: true });
    observerRef.current = observer;
}


async function applyColorTheme(plugin, cartoonRepRef, themeName, themeParams = {}) {
    const params = createStructureRepresentationParams(plugin, cartoonRepRef.obj?.data.sourceData, {
        type: "cartoon",
        color: themeName,
        colorParams: themeParams,
        size: "uniform",
    });
    await plugin.build().to(cartoonRepRef).update(params).commit();
}

async function resetCartoonColor(plugin, cartoonRepRef, color = ColorNames.white) {
    const params = createStructureRepresentationParams(plugin, cartoonRepRef.obj?.data.sourceData, {
        type: 'cartoon',
        color: 'uniform',
        colorParams: { value: color }
    });
    await plugin.build().to(cartoonRepRef).update(params).commit();
}

async function applyOverpaintByResidueScript(plugin, cartoonRepRef, data, getScriptAndColorFn, baseColor = Color(0XFAFAFA)) {
    await resetCartoonColor(plugin, cartoonRepRef, baseColor);

    const layers = data.map(item => getScriptAndColorFn(item, 'mol-script'))
                       .filter(layer => layer !== null && layer.script && layer.color);

    if (layers.length > 0) {
        await plugin.build()
            .to(cartoonRepRef)
            .apply(StateTransforms.Representation.OverpaintStructureRepresentation3DFromScript, { layers })
            .commit();
    }
}

function createLociLabelProviderGeneric(residueDataMap, formatLabelFn) {
    return {
        label: (loci) => {
            if (StructureElement.Loci.is(loci)) {
                const loc = StructureElement.Loci.getFirstLocation(loci);
                if (loc) {
                    const position = StructureProperties.residue.auth_seq_id(loc);
                    const matchingResidue = residueDataMap.get(position);
                    if (matchingResidue) {
                        return formatLabelFn(matchingResidue);
                    }
                }
            }
            return undefined;
        },
    };
}

async function addRepresentationForSelection(plugin, structureRef, selectionExpression, reprParams, label) {
     await plugin.build()
        .to(structureRef)
        .apply(StateTransforms.Model.StructureSelectionFromExpression, { label, expression: selectionExpression })
        .apply(StateTransforms.Representation.StructureRepresentation3D, reprParams)
        .commit();
}

async function loadPdbForSuperpositionTrigger(plugin, pdbIdToLoad) {
    try {
        const pdbData = await plugin.builders.data.download(
            { url: `https://www.ebi.ac.uk/pdbe/entry-files/${pdbIdToLoad.toLowerCase()}_updated.cif` },
            { state: { isGhost: true } }
        );
        const pdbTraj = await plugin.builders.structure.parseTrajectory(pdbData, 'mmcif');
        const preset = await plugin.builders.structure.hierarchy.applyPreset(pdbTraj, 'default');

       const cartoonPDB = preset.representation.representations.polymer;

       const reprParams = createStructureRepresentationParams(plugin, undefined, {
            type: 'cartoon',
            color: 'uniform',
            colorParams: { value: ColorNames.gray }
        });

       await plugin.build().to(cartoonPDB).update(reprParams).commit();

       const input = plugin.managers.structure.hierarchy.behaviors.selection.value.structures;
       const structures = input.map(s => s.cell?.obj?.data);
       const traceOnly = true;

       let { entries, failedPairs, zeroOverlapPairs } =  alignAndSuperposeWithSIFTSMapping(structures,
       {  traceOnly,
          applyTestIndex: [1] }
        );

       const coordinateSystem = input[0]?.cell.obj?.data?.state?.coordinateSystem;
        let rmsd = 0;

        for (const xform of entries) {
            await transform(input[xform.other].cell, xform.transform.bTransform, coordinateSystem, plugin);
            rmsd += xform.transform.rmsd;
        }

        rmsd /= Math.max(entries.length - 1, 1);

        PluginCommands.Toast.Show(plugin, {
        title: 'PDB Superposition',
        message: entries.length === 0
            ? 'No superposition between given structures'
            : 'RMSD (Å) ' + rmsd.toFixed(2),
        key: 'toast-custom',
       });

    } catch (error) {
        console.error(`Loading PDB ${pdbIdToLoad} for superposition failed:`, error);
    }
}


// REVISED HANDLERS
async function handleUnDomainsAnnotation({ plugin, mainCartoonRepRef, dataResidue, colors, viewerRootElement, instanceId, observerRef, targetChain }) {
    const residueDta = dataResidue
        .filter(r => r.unDomains.trim() !== "-")
        .map(r => ({ ...r, anPosition: parseInt(r.anPosition.split("_")[1], 10) }));

    await applyOverpaintByResidueScript(plugin, mainCartoonRepRef, residueDta, (item, lang) => ({
        script: Script(`(sel.atom.atom-groups :chain-test (= atom.auth_asym_id ${item.chain || 'A'}) :residue-test (in-range atom.resno ${item.anPosition} ${item.anPosition}))`, lang),
        color: Color("0x" + colors[item.unDomains])
    }), ColorNames.white);

    const stylingFn = (scopeRoot) => {
        residueDta.forEach(item => {
            const position = item.anPosition - 1;
            const span = scopeRoot.querySelector(`span[data-seqid="${position}"]`);
            if (span) {
                const colorKey = item.unDomains.trim();
                const hexColor = colors[colorKey];
                if (hexColor) {
                    const sanitizedKey = String(colorKey).replace(/\s+/g, '-').replace(/[^a-zA-Z0-9_-]/g, '');
                    const className = `molstar-seq-highlight-${sanitizedKey}`;
                    const styleId = `${DYNAMIC_STYLE_ID_PREFIX_BASE}${instanceId}-${className}`;

                    if (!document.getElementById(styleId)) {
                        const style = document.createElement("style");
                        style.id = styleId;
                        style.innerHTML = `.${className} { background-color: #${hexColor} !important; }`;
                        document.head.appendChild(style);
                    }
                    span.classList.forEach(cls => { if (cls.startsWith('molstar-seq-highlight-')) span.classList.remove(cls); });
                    span.classList.add(className);
                }
            }
        });
    };

    observeAndStyleSequence(viewerRootElement, stylingFn, observerRef, targetChain);
}

async function handleAAmscore({ plugin, mainCartoonRepRef, dataResidue, viewerRootElement, instanceId, observerRef, targetChain }) {

    const residueDta = dataResidue
        .filter(r => r.aamissense && r.aamissense.amMean !== undefined && r.aamissense.amMean !== null)
        .map(r => ({
            ...r,
            amMean: parseFloat(r.aamissense.amMean),
            anPosition: parseInt(r.anPosition.split("_")[1], 10)
        }))
        .filter(r => !isNaN(r.amMean));

    if (residueDta.length === 0) return;

    const getAmColorHex = (score) => {
        if (score >= 0.564) return "db0909"; // Likely Pathogenic
        if (score > 0.340)  return "f1c40f"; // Ambiguous
        return "0984e3";                     // Likely Benign
    };

    await applyOverpaintByResidueScript(plugin, mainCartoonRepRef, residueDta, (item, lang) => {
        const hexColor = getAmColorHex(item.amMean);
        return {
            script: Script(`(sel.atom.atom-groups :chain-test (= atom.auth_asym_id ${item.chain || 'A'}) :residue-test (in-range atom.resno ${item.anPosition} ${item.anPosition}))`, lang),
            color: Color("0x" + hexColor)
        };
    }, ColorNames.white);

    const stylingFn = (scopeRoot) => {
        residueDta.forEach(item => {
            const position = item.anPosition - 1; // O Mol* baseia a sequência 1D em index 0
            const span = scopeRoot.querySelector(`span[data-seqid="${position}"]`);

            if (span) {
                const hexColor = getAmColorHex(item.amMean);
                const className = `molstar-seq-ammean-${hexColor}`;
                const styleId = `${DYNAMIC_STYLE_ID_PREFIX_BASE}${instanceId}-${className}`;

                if (!document.getElementById(styleId)) {
                    const style = document.createElement("style");
                    style.id = styleId;
                    style.innerHTML = `.${className} { background-color: #${hexColor} !important; }`;
                    document.head.appendChild(style);
                }

                span.classList.forEach(cls => {
                    if (cls.startsWith('molstar-seq-ammean-') || cls.startsWith('molstar-seq-highlight-')) {
                        span.classList.remove(cls);
                    }
                });
                span.classList.add(className);
            }
        });
    };
    observeAndStyleSequence(viewerRootElement, stylingFn, observerRef, targetChain);

    const residueMap = new Map(residueDta.map(r => [r.anPosition, r]));
    plugin.managers.lociLabels.addProvider(createLociLabelProviderGeneric(residueMap, item => {
        let category = "Likely Benign";
        if (item.amMean >= 0.564) category = "Likely Pathogenic";
        else if (item.amMean > 0.340) category = "Ambiguous";

        return `AlphaMissense: ${item.amMean} (${category})`;
    }));
}


async function handlePlddtAnnotation({ plugin, mainCartoonRepRef }) {
    await applyColorTheme(plugin, mainCartoonRepRef, "uncertainty", {
        domain: [0, 100], list: { colors: [Color(0x0053d6), Color(0x65cbf3), Color(0xffdb13), Color(0xff7d45)] }
    });
    plugin.managers.lociLabels.addProvider({
        label: (loci) => {
            if (StructureElement.Loci.is(loci)) {
                const loc = StructureElement.Loci.getFirstLocation(loci);
                if (loc) {
                    const bFactor = StructureProperties.atom.B_iso_or_equiv(loc);
                    return `pLDDT: ${bFactor}`;
                }
            }
            return undefined;
        }
    });
}

/**
 * AlphaFold-predicted secondary structure.
 * The value comes from `afPredictedSecondaryStructure` (or, for legacy rows,
 * `afPredSecStruct`) as a DSSP-style label like "Alpha helix [DSSP:H]".
 */
async function handleAfPredictedSecondaryStructure({
    plugin, mainCartoonRepRef, dataResidue, viewerRootElement, instanceId, observerRef, targetChain,
}) {
    // Accept either field name in the data (some pipelines rename it)
    const field = dataResidue?.[0]?.afPredictedSecondaryStructure !== undefined
        ? 'afPredictedSecondaryStructure'
        : 'afPredSecStruct';

    await handleMultiColorByRangeAnnotation({
        plugin, mainCartoonRepRef, dataResidue,
        annotationKey: field,
        getColorHexFn: (item) => matchByKeyword(item.data, AF_SECONDARY_STRUCTURE_KEYWORDS, null),
        viewerRootElement, instanceId, observerRef, targetChain,
    });
}

async function handleUnAnnNatVariantsAnnotation({ plugin, mainStructureRef, mainCartoonRepRef, dataResidue }) {

    const residueDta = dataResidue
        .filter(r => r.unAnnNatVariants.trim() !== "-")
        .map(r => ({ ...r, variant: r.unAnnNatVariants.trim(), anPosition: parseInt(r.anPosition.split("_")[1], 10) }));

    const uniquePositions = [...new Set(residueDta.map(r => r.anPosition))];
    const uniqueChains = [...new Set(residueDta.map(r => r.chain || 'A'))];

    await resetCartoonColor(plugin, mainCartoonRepRef, Color(0XFAFAFA));

    const selection = MS.struct.generator.atomGroups({
        "chain-test": MS.core.set.has([MS.set(...uniqueChains), MS.struct.atomProperty.macromolecular.auth_asym_id()]),
        'residue-test': MS.core.set.has([MS.set(...uniquePositions), MS.ammp('auth_seq_id')]),
        'atom-test': MS.core.set.has([MS.set('CA'), MS.ammp('label_atom_id')]),
        'group-by': MS.struct.atomProperty.macromolecular.residueKey(),
    });
    const reprParams = createStructureRepresentationParams(plugin, undefined, {
        type: 'spacefill', color: 'uniform', colorParams: { value: Color(VARIANT_SPACEFILL_COLOR_HEX) },
        size: 'physical', sizeParams: { scale: 1 }
    });
    await addRepresentationForSelection(plugin, mainStructureRef, selection, reprParams, 'Variant Residues');

    const residueMap = new Map(residueDta.map(r => [r.anPosition, r]));
    plugin.managers.lociLabels.addProvider(createLociLabelProviderGeneric(residueMap, item => {
        let label = item.variant;
        if (label.includes("VAR")) label += " (Missense)";
        else if (label.includes("del")) label += " (Deletion)";
        else if (label.includes("I")) label += " (Insertion)";
        return `Variant: ${label}`;
    }));
}

/**
 * Generic list annotation (single or per-item color).
 *
 * If `perItemColorFn` is provided, it receives the item's `data` string and
 * must return a hex string (no '#'). This allows sub-type coloring for
 * glycosylations, lipidations, etc. Otherwise, the track-wide `highlightColor`
 * + `seqColor` pair is used.
 */
async function handleGenericResidueListAnnotation(
    { plugin, mainCartoonRepRef, dataResidue, annotationKey, labelPrefix, highlightColor = Color(0x008000),
      viewerRootElement, instanceId, observerRef, seqColor, perItemColorFn = null, targetChain }
) {
    const residueDta = dataResidue
        .filter(r => r[annotationKey] && String(r[annotationKey]).trim() !== "-")
        .map(r => ({ ...r, data: String(r[annotationKey]).trim(), anPosition: parseInt(r.anPosition.split("_")[1], 10) }));

    if (residueDta.length === 0) return;

    const positions = [...new Set(residueDta.map(r => r.anPosition))];
    if (positions.length === 0) return;

    await applyOverpaintByResidueScript(plugin, mainCartoonRepRef, residueDta, (item, lang) => {
        const hex = perItemColorFn ? perItemColorFn(item.data) : null;
        const color = hex ? Color('0x' + hex) : highlightColor;
        return {
            script: Script(`(sel.atom.atom-groups :chain-test (= atom.auth_asym_id ${item.chain || 'A'}) :residue-test (set.has (set ${item.anPosition}) atom.auth_seq_id))`, lang),
            color,
        };
    });

    if (labelPrefix) {
        const residueMap = new Map(residueDta.map(r => [r.anPosition, r]));
        plugin.managers.lociLabels.addProvider(createLociLabelProviderGeneric(residueMap, item => `${labelPrefix}: ${item.data}`));
    }

    const stylingFn = (scopeRoot) => {
        residueDta.forEach(item => {
            const posNew = item.anPosition - 1;
            const span = scopeRoot.querySelector(`span[data-seqid="${posNew}"]`);
            if (!span) return;

            const hex = perItemColorFn ? perItemColorFn(item.data) : seqColor;
            // Distinct class per color so multiple sub-colors can coexist
            const classSuffix = perItemColorFn ? `${annotationKey}-${hex}` : annotationKey;
            const className = `molstar-seq-highlight-${classSuffix}`;
            const styleId = `${DYNAMIC_STYLE_ID_PREFIX_BASE}${instanceId}-${className}`;

            if (!document.getElementById(styleId)) {
                const style = document.createElement("style");
                style.id = styleId;
                style.innerHTML = `.${className} { background-color: #${hex} !important; }`;
                document.head.appendChild(style);
            }
            span.classList.forEach(cls => { if (cls.startsWith('molstar-seq-highlight-')) span.classList.remove(cls); });
            span.classList.add(className);
        });
    };
    observeAndStyleSequence(viewerRootElement, stylingFn, observerRef, targetChain);
}


async function handlePhosPtmsAnnotation({ plugin, mainCartoonRepRef, dataResidue, viewerRootElement, instanceId, observerRef, targetChain }) {
    const residueDta = dataResidue
        .filter(r => r.phosPtms.trim() !== "-")
        .map(r => ({ ...r, ptm: r.phosPtms.trim(), anPosition: parseInt(r.anPosition.split("_")[1], 10) }));

    if (residueDta.length === 0) return;

    const positions = [...new Set(residueDta.map(r => r.anPosition))];
    if (positions.length === 0) return;

    await applyOverpaintByResidueScript(plugin, mainCartoonRepRef, residueDta, (item, lang) => {
        const colorHex = getPtmColorHex(item.ptm);
        return {
             script: Script(`(sel.atom.atom-groups :chain-test (= atom.auth_asym_id ${item.chain || 'A'}) :residue-test (set.has (set ${item.anPosition}) atom.auth_seq_id))`, lang),
            color: Color("0x" + colorHex)
        };
    });

    const stylingFn = (scopeRoot) => {
        residueDta.forEach(({ ptm, anPosition }) => {
            const pos = anPosition - 1;
            const span = scopeRoot.querySelector(`span[data-seqid="${pos}"]`);
            if (!span) return;

            const colorHex = getPtmColorHex(ptm);
            // Class keyed by color so all PTM subtypes get their own rule
            const className = `molstar-seq-ptm-${colorHex}`;
            const styleId = `${DYNAMIC_STYLE_ID_PREFIX_BASE}${instanceId}-${className}`;

            if (!document.getElementById(styleId)) {
                const style = document.createElement("style");
                style.id = styleId;
                style.innerHTML = `.${className} { background-color: #${colorHex} !important; }`;
                document.head.appendChild(style);
            }
            span.classList.forEach(cls => { if (cls.startsWith('molstar-seq-ptm-')) span.classList.remove(cls); });
            span.classList.add(className);
        });
    };
    observeAndStyleSequence(viewerRootElement, stylingFn, observerRef, targetChain);

    const residueMap = new Map(residueDta.map(r => [r.anPosition, r]));
    plugin.managers.lociLabels.addProvider(createLociLabelProviderGeneric(residueMap, item => `PTMs: ${item.ptm}`));
}

/**
 * Paint residues by a per-item color resolver.
 *
 * @param getColorHexFn  (item) => hex | null
 *    Receives the residue row (with `.data` = the annotation value) and
 *    must return a hex string without '#', or null to skip painting.
 */
async function handleMultiColorByRangeAnnotation({
    plugin, mainCartoonRepRef, dataResidue, annotationKey,
    getColorHexFn, viewerRootElement, instanceId, observerRef, targetChain,
}) {
    const residueDta = dataResidue
        .filter(r => r[annotationKey] && String(r[annotationKey]).trim() !== "-")
        .map(r => ({ ...r, data: String(r[annotationKey]).trim(), anPosition: parseInt(r.anPosition.split("_")[1], 10) }));

    if (residueDta.length === 0) return;

    await applyOverpaintByResidueScript(plugin, mainCartoonRepRef, residueDta, (item, lang) => {
        const hexColor = getColorHexFn(item);
        if (!hexColor) return null;
        return {
            script: Script(`(sel.atom.atom-groups :chain-test (= atom.auth_asym_id ${item.chain || 'A'}) :residue-test (in-range atom.resno ${item.anPosition} ${item.anPosition}))`, lang),
            color: Color("0x" + hexColor)
        };
    }, ColorNames.white);

    const stylingFn = (scopeRoot) => {
        residueDta.forEach(item => {
            const position = parseInt(item.anPosition) - 1;
            const span = scopeRoot.querySelector(`span[data-seqid="${position}"]`);
            if (!span) return;

            const hexColor = getColorHexFn(item);
            if (!hexColor) return;

            // Class name keyed by the color so distinct categories get
            // distinct CSS rules automatically.
            const className = `molstar-seq-highlight-${annotationKey}-${hexColor}`;
            const styleId = `${DYNAMIC_STYLE_ID_PREFIX_BASE}${instanceId}-${className}`;

            if (!document.getElementById(styleId)) {
                const style = document.createElement("style");
                style.id = styleId;
                style.innerHTML = `.${className} { background-color: #${hexColor} !important; }`;
                document.head.appendChild(style);
            }
            span.classList.forEach(cls => { if (cls.startsWith('molstar-seq-highlight-')) span.classList.remove(cls); });
            span.classList.add(className);
        });
    };
    observeAndStyleSequence(viewerRootElement, stylingFn, observerRef, targetChain);
}

async function handleAlphaFillAnnotation({ plugin, mainCartoonRepRef, mainStructureData, ligands }) {
    await handlePlddtAnnotation({ plugin, mainCartoonRepRef });

    if (ligands && ligands.length > 0 && mainStructureData) {
        const ligandSelectionScript = MS.struct.generator.atomGroups({
            'residue-test': MS.core.set.has([MS.set(...ligands), MS.ammp('label_comp_id')]),
            'group-by': MS.struct.atomProperty.macromolecular.residueKey(),
        });
        const selection = Script.getStructureSelection(q => ligandSelectionScript, mainStructureData);
        if (selection && selection.kind !== 'empty-loci') {
            const loci = StructureSelection.toLociWithSourceUnits(selection);
            plugin.managers.interactivity.lociSelects.selectOnly({ loci });
        }
    }
}

async function handleInterProAnnotation({ plugin, mainCartoonRepRef, domainInterPro, viewerRootElement, instanceId, observerRef, ModelType, targetChain }) {
    if (!domainInterPro || domainInterPro.length === 0) return;
    await resetCartoonColor(plugin, mainCartoonRepRef, ColorNames.white);

    const layers = domainInterPro.map(domain => ({
        script: Script(`(sel.atom.atom-groups :chain-test (= atom.auth_asym_id ${domain.chain}) :residue-test (in-range atom.resno ${domain.start} ${domain.end}))`, 'mol-script'),
        color: Color("0x" + domain.color),
        clear: false
    }));

    if (layers.length > 0) {
        await plugin.build().to(mainCartoonRepRef).apply(StateTransforms.Representation.OverpaintStructureRepresentation3DFromScript, { layers }).commit();
    }

    const stylingFn = (scopeRoot) => {
        domainInterPro.forEach(domain => {
            const start = parseInt(domain.start, 10) - 1;
            const stop = parseInt(domain.end, 10) - 1;
            const color = domain.color;
            const sanitizedKey = domain.domainName.replace(/\s+/g, '-').replace(/[^a-zA-Z0-9_-]/g, '');
            const className = `molstar-seq-highlight-interpro-${sanitizedKey}`;
            const styleId = `${DYNAMIC_STYLE_ID_PREFIX_BASE}${instanceId}-${className}`;

            if (!document.getElementById(styleId)) {
                const style = document.createElement("style");
                style.id = styleId;
                style.innerHTML = `.${className} { background-color: #${color} !important; }`;
                document.head.appendChild(style);
            }
            for (let pos = start; pos <= stop; pos++) {
                const span = scopeRoot.querySelector(`span[data-seqid="${pos}"]`);
                if (span) {
                    span.classList.forEach(cls => { if (cls.startsWith('molstar-seq-highlight-')) span.classList.remove(cls); });
                    span.classList.add(className);
                }
            }
        });
    };
    observeAndStyleSequence(viewerRootElement, stylingFn, observerRef, targetChain);
}


async function handleGaussianAnnotation({ plugin, mainPolymerComponentRef, mainCartoonRepRef }) {
    await applyColorTheme(plugin, mainCartoonRepRef, 'secondary-structure');

    const surfaceParams = createStructureRepresentationParams(plugin, undefined, {
        type: 'molecular-surface',
        color: 'uniform',
        colorParams: { value: Color(0xd0f5ff) },
    });
    const surfaceStateObj = await plugin.build()
        .to(mainPolymerComponentRef)
        .apply(StateTransforms.Representation.StructureRepresentation3D, surfaceParams)
        .commit();

    if (surfaceStateObj.cell) {
        const transparencyParams = {
            layers: [{
                script: Script(`(sel.atom.atom-groups :chain-test (= atom.auth_asym_id A ))`, 'mol-script'),
                value: 0.5
            }]
        };
        await plugin.build()
            .to(surfaceStateObj.cell)
            .apply(StateTransforms.Representation.TransparencyStructureRepresentation3DFromScript, transparencyParams)
            .commit();
    }
}


const MolstarViewerFunctional = ({ annotation, dataResidue, database, colors, ligands, setLigandsOption, domainInterPro, superpose, pdbId, multimerLink, modelType }) => {

    const viewerRef = useRef(null);
    const pluginInstanceRef = useRef(null);
    const observerRef = useRef(null);
    const uniac = dataResidue?.[0]?.anPosition?.split("_")[0];

    const instanceId = useMemo(() => {
        if (!uniac) return `temp-instance-${Math.random().toString(36).substr(2, 9)}`;
        return `molstar-instance-${uniac}-${Math.random().toString(36).substr(2, 9)}`;
    }, [uniac]);


    const initMolstar = useMemo(() => async () => {
        if (!viewerRef.current || pluginInstanceRef.current || !uniac) return;

        const plugin = await createPluginUI({
            target: viewerRef.current,
            layoutIsExpanded: false, layoutShowControls: false, layoutShowRemoteState: false,
            layoutShowSequence: true, layoutShowLog: false, layoutShowLeftPanel: false,
            viewportShowExpand: false, viewportShowSelectionMode: false, viewportShowAnimation: false,
            backgroundColor: ColorNames.white,
            render: renderReact18,
        });
        pluginInstanceRef.current = plugin;
        PluginCommands.Canvas3D.SetSettings(plugin, { settings: { renderer: { backgroundColor: ColorNames.white } } });

        const viewerRootElement = viewerRef.current;

        try {

            const urlStructure = (modelType === 'Multimer' && multimerLink)
            ? multimerLink
            : (database === 'AF' && annotation !== 'AlphaFill'
                ? `https://alphafold.ebi.ac.uk/files/AF-${uniac}-F1-model_v6.cif`
                : `https://alphafill.eu/v1/aff/${uniac}`);

            const data = await plugin.builders.data.download({ url: urlStructure }, { state: { isGhost: true } });
            const trajectory = await plugin.builders.structure.parseTrajectory(data, 'mmcif');
            const preset = await plugin.builders.structure.hierarchy.applyPreset(trajectory, 'default', {
                showUnitcell: false, representationPreset: 'auto',
            });

            const mainStructureHierarchy = plugin.managers.structure.hierarchy.current.structures[0];
            if (!mainStructureHierarchy?.cell?.obj?.data) {
                console.error("Failed to load main structure data.");
                return;
            }
            const mainStructureData = mainStructureHierarchy.cell.obj.data;
            const mainStructureRef = mainStructureHierarchy.cell;

            const mainPolymerComponentRef = preset.representation?.components?.polymer?.cell;
            const mainCartoonRepRef = preset.representation?.representations?.polymer?.cell;

            if (!mainPolymerComponentRef || !mainCartoonRepRef) {
                console.error("Main polymer component or cartoon representation not found.");
                return;
            }

            const chemicalComponentMap = mainStructureData.model.properties.chemicalComponentMap;
            const aminoAcids = ["ALA", "ARG", "ASN", "ASP", "CYS", "GLN", "GLU", "GLY", "HIS", "ILE", "LEU", "LYS", "MET", "PHE", "PRO", "SER", "TRP", "THR", "TYR", "VAL"];
            const detectedLigands = Array.from(chemicalComponentMap.keys()).filter(key => !aminoAcids.includes(key));
            setLigandsOption(prev => JSON.stringify(prev) !== JSON.stringify(detectedLigands) ? detectedLigands : prev);

            // Chain the residue annotations belong to. A single distinct
            // chain (heterodimer target) is enforced in the sequence panel;
            // null (homodimer A+B / monomer default) keeps the whole panel.
            const targetChainSet = [...new Set((dataResidue || []).map(r => r.chain || 'A'))];
            const targetChain = targetChainSet.length === 1 ? targetChainSet[0] : null;

            const commonArgs = { plugin, mainStructureData, mainStructureRef, mainPolymerComponentRef, mainCartoonRepRef, dataResidue, colors, domainInterPro, ligands, viewerRootElement, instanceId, observerRef, targetChain };

            if (annotation === "unDomains") await handleUnDomainsAnnotation(commonArgs);
            else if (annotation === "pLDDT") await handlePlddtAnnotation(commonArgs);
            else if (annotation === "amMean") await handleAAmscore(commonArgs);
            else if (annotation === "afPredictedSecondaryStructure") await handleAfPredictedSecondaryStructure(commonArgs);
            else if (annotation === "unAnnNatVariants") await handleUnAnnNatVariantsAnnotation(commonArgs);
            else if (annotation === "phosPtms") await handlePhosPtmsAnnotation(commonArgs);
            else if (annotation === "unSecondaryStructure") await handleMultiColorByRangeAnnotation({
                ...commonArgs,
                annotationKey: 'unSecondaryStructure',
                getColorHexFn: (item) => matchByKeyword(item.data, SECONDARY_STRUCTURE_KEYWORDS, null),
            });
            else if (annotation === "unLocation") await handleMultiColorByRangeAnnotation({
                ...commonArgs,
                annotationKey: 'unLocation',
                getColorHexFn: (item) => matchByKeyword(item.data, LOCATION_KEYWORDS, null),
            });
            else if (annotation === "unMoleculeProcessing") await handleMultiColorByRangeAnnotation({
                ...commonArgs,
                annotationKey: 'unMoleculeProcessing',
                getColorHexFn: (item) => matchByKeyword(item.data, MOLECULE_PROCESSING_KEYWORDS, null),
            });
            else if (annotation === "surface") await applyColorTheme(plugin, mainCartoonRepRef, 'accessible-surface-area');
            else if (annotation === "hydrophobicity") await applyColorTheme(plugin, mainCartoonRepRef, 'hydrophobicity');
            else if (annotation === "name") await applyColorTheme(plugin, mainCartoonRepRef, 'residue-name');
            else if (annotation === "rainbow") await applyColorTheme(plugin, mainCartoonRepRef, 'sequence-id');
            else if (annotation === "AlphaFill") await handleAlphaFillAnnotation(commonArgs);
            else if (annotation === "interPro") await handleInterProAnnotation(commonArgs);
            else if (annotation === "gaussian") await handleGaussianAnnotation(commonArgs);
            else if (["unMotives", "pfamDomainPosHmm", "unLigandsBindingSite", "unAnnPropActiveSite", "unGlycosylations", "unLipidations", "unModifiedRes", "unDisulfideBongBridges", "unCrossLinks", "inBindingRegion"].includes(annotation)) {
                let labelPrefix, highlightColor, seqColor;
                let perItemColorFn = null; // for sub-typed tracks (glyco / lipid)

                if (annotation === "unMotives") { labelPrefix = "Motive"; highlightColor = Color(0xe63946); seqColor = "e63946"; }
                else if (annotation === "pfamDomainPosHmm") { labelPrefix = "Pfam"; highlightColor = Color(0xaa1c7d); seqColor = "aa1c7d"; }
                else if (annotation === "unLigandsBindingSite") { labelPrefix = "Ligand"; highlightColor = Color(0x00b159); seqColor = "00b159"; }
                else if (annotation === "unAnnPropActiveSite") { labelPrefix = "Active site"; highlightColor = Color(0xf37735); seqColor = "f37735"; }
                else if (annotation === "unGlycosylations") {
                    labelPrefix = "Glycosylation";
                    highlightColor = Color(0x56ab2f);
                    seqColor = GLYCOSYLATION_DEFAULT_HEX;
                    perItemColorFn = (value) => matchByKeyword(value, GLYCOSYLATION_KEYWORDS, GLYCOSYLATION_DEFAULT_HEX);
                }
                else if (annotation === "unLipidations") {
                    labelPrefix = "Lipidation";
                    highlightColor = Color(0x007f5f);
                    seqColor = LIPIDATION_DEFAULT_HEX;
                    perItemColorFn = (value) => matchByKeyword(value, LIPIDATION_KEYWORDS, LIPIDATION_DEFAULT_HEX);
                }
                else if (annotation === "unModifiedRes") { labelPrefix = "Modified Residue"; highlightColor = Color(0xffc425); seqColor = "ffc425"; }
                else if (annotation === "unDisulfideBongBridges") { labelPrefix = "Disulfide bond"; highlightColor = Color(0x8338ec); seqColor = "8338ec"; }
                else if (annotation === "unCrossLinks") { labelPrefix = "CrossLink"; highlightColor = Color(0x00aedb); seqColor = "00aedb"; }
                else if (annotation === "inBindingRegion") { labelPrefix = "Binding Region"; highlightColor = Color(0xff006e); seqColor = "ff006e"; }

                await handleGenericResidueListAnnotation({
                    ...commonArgs,
                    annotationKey: annotation,
                    labelPrefix, highlightColor, seqColor, perItemColorFn,
                });
            }

            if (pdbId !== "" && pdbId !== "Select a PDB" && pdbId !== "N/A" && superpose) {
                await loadPdbForSuperpositionTrigger(plugin, pdbId);
            }

        } catch (error) {
            console.error('Molstar initialization or structure processing failed:', error);
        }
    }, [uniac, dataResidue, database, annotation, colors, ligands, setLigandsOption, domainInterPro, superpose, pdbId, instanceId, modelType, multimerLink]);

    useEffect(() => {
        if (uniac && viewerRef.current) {
          initMolstar();
        }

        return () => {
            if (pluginInstanceRef.current) {
                pluginInstanceRef.current.dispose();
                pluginInstanceRef.current = null;
            }
            if (observerRef.current) {
                observerRef.current.disconnect();
                observerRef.current = null;
            }
            const styleSelector = `style[id^="${DYNAMIC_STYLE_ID_PREFIX_BASE}${instanceId}-"]`;
            document.querySelectorAll(styleSelector).forEach(style => style.remove());
        };
    }, [initMolstar, uniac, instanceId, modelType, multimerLink]);

    return <div key={`${uniac}-${instanceId}`} ref={viewerRef} style={{ width: '100%', height: '100%' }} />;
};

export default MolstarViewerFunctional;
